import { api } from '@/lib/api';
import { ReportType } from './types';

// Generic response type
interface ReportApiResponse {
  result: string;
  link: string;
}

// What the caller actually passes — the function hardcodes the rest
interface ReportPayloadInput {
  start_date: string;
  end_date: string;
  create_suppression_list?: boolean;
  create_phone_list?: boolean;
  suppression_list_name?: string;
  phone_list_name?: string;
}

// Params for the API generation function
interface GenerateReportParams {
  type: ReportType;
  accountId?: string;
  jobId?: string;
  payload: ReportPayloadInput;
}

export const generateReport = async ({
  type,
  accountId,
  jobId,
  payload,
}: GenerateReportParams): Promise<ReportApiResponse> => {
  let endpoint = '';
  const queryParams = new URLSearchParams();

  // Common params
  if (accountId) queryParams.append('account_id', accountId);
  queryParams.append('date_range', 'CUSTOM');
  queryParams.append('start_date', payload.start_date);
  queryParams.append('end_date', payload.end_date);
  queryParams.append('justsurveys', 'false');
  queryParams.append('justlastpass', 'false');
  queryParams.append('show_headers', 'true');
  queryParams.append('reportformat', 'csv');

  switch (type) {
    case ReportType.JOBS_REPORT:
      endpoint = '/1to1/jobs/statsreport';
      break;

    case ReportType.MESSAGE_DOWNLOAD:
      if (!jobId) throw new Error('Job ID is required for Message Download report');
      endpoint = `/1to1/jobs/${jobId}/cdrs`;
      appendJobReportParams(queryParams, payload);
      break;

    case ReportType.QUESTION_RESPONSES:
      if (!jobId) throw new Error('Job ID is required for Question Responses report');
      endpoint = `/1to1/jobs/${jobId}/questionresponses`;
      appendJobReportParams(queryParams, payload);
      break;

    case ReportType.TRACKED_LINK_CLICKS:
      if (!jobId) throw new Error('Job ID is required for Tracked Link Clicks report');
      endpoint = `/1to1/jobs/${jobId}/trackedlinkclicks`;
      appendJobReportParams(queryParams, payload);
      break;

    case ReportType.AI_SUPPORT:
      if (!jobId) throw new Error('Job ID is required for AI Support report');
      endpoint = `/1to1/jobs/${jobId}/ai_support_report`;
      appendJobReportParams(queryParams, payload);
      break;

    default:
      throw new Error(`Report type ${type} is not implemented`);
  }

  // Construct final URL with query params
  const url = `${endpoint}?${queryParams.toString()}`;

  return api.get<ReportApiResponse>(url).then((res) => res.data);
};

const appendJobReportParams = (params: URLSearchParams, payload: ReportPayloadInput) => {
  params.append('create_suppression_list', String(payload.create_suppression_list));
  params.append('create_phone_list', String(payload.create_phone_list));

  if (payload.suppression_list_name) {
    params.append('suppression_list_name', payload.suppression_list_name);
  }
  if (payload.phone_list_name) {
    params.append('phone_list_name', payload.phone_list_name);
  }
};
